import { Button, SearchableSelect } from '@/shared/components'
import {
  ENROLLMENT_LOCATION_MESSAGES,
  ENROLLMENT_SUPPORT_PHONE
} from '../../constants/enrollmentContent'
import { StepHeader } from '../StepHeader'
import { LocationSelectionStatus } from './locationSelection/LocationSelectionStatus'
import { TransmissionTypeSelector } from './locationSelection/TransmissionTypeSelector'
import { useLocationSelectionControls } from './locationSelection/useLocationSelectionControls'
import { useLocationSelectionStep } from './locationSelection/useLocationSelectionStep'
import type { EligibilityFormData } from './types'
import type { EnrollmentFieldEditability } from '../../utils/enrollmentHydration'

type LocationSelectionStepProps = {
  currentStep: number
  totalSteps: number
  fieldEditability: EnrollmentFieldEditability
  formData: EligibilityFormData
  updateFormData: (data: Partial<EligibilityFormData>) => void
  onBack: () => void
  onContinue: (locationValue?: string) => void
  errorMessage?: string | null
  warningMessage?: string | null
  continueDisabled?: boolean
}

export function LocationSelectionStep({
  currentStep,
  totalSteps,
  fieldEditability,
  formData,
  updateFormData,
  onBack,
  onContinue,
  errorMessage = null,
  warningMessage = null,
  continueDisabled = false
}: LocationSelectionStepProps) {
  const isLocationDisabled =
    fieldEditability.trainingLocationExternalId === false ||
    fieldEditability.locationExternalId === false
  const isTransmissionDisabled = fieldEditability.preferredTransmission === false

  const {
    zipCode,
    isPending,
    isError,
    isLoadingLocations,
    isMissingDriverLicenseState,
    hasNoLocations,
    hasNoLicenseStateLocations,
    isLocationSelectDisabled,
    driverLicenseStateName,
    locationOptions,
    effectiveLocation,
    selectedLocation,
    selectedTransmission,
    bothAvailable,
    locationPlaceholder,
    handleLocationChange
  } = useLocationSelectionStep({
    formData,
    isLocationLocked: isLocationDisabled,
    updateFormData
  })

  const {
    handleContinueClick,
    handleTransmissionChange,
    isContinueButtonDisabled,
    transmissionError
  } = useLocationSelectionControls({
    bothAvailable,
    continueDisabled,
    effectiveLocation,
    formData,
    isError,
    isPending,
    onContinue,
    selectedLocation,
    updateFormData
  })

  return (
    <div className="bg-white px-4 py-2">
      <div className="mx-auto max-w-4xl animate-fade-in-up">
        <StepHeader currentStep={currentStep} totalSteps={totalSteps} onBack={onBack} />

        <div className="mb-5 flex items-start justify-center animate-fade-in-up">
          <h1 className="modal-heading__title text-center">Training Location Selection</h1>
        </div>

        <div className="mx-auto max-w-md space-y-4 pt-6">
          {errorMessage ? <p className="text-sm text-red-600">{errorMessage}</p> : null}
          {warningMessage ? <p className="text-sm text-amber-700">{warningMessage}</p> : null}
          <div className="animate-fade-in-up" style={{ animationDelay: '120ms' }}>
            <label className="enrollment-field-label">
              Zip Code <span className="text-red-500">*</span>
            </label>
            <input
              id="zip"
              type="text"
              value={zipCode}
              placeholder="Pre-filled"
              inputMode="numeric"
              maxLength={5}
              disabled
              readOnly
              className="enrollment-field-control cursor-not-allowed bg-gray-100 text-gray-500"
            />
            <p className="mt-2 text-xs leading-5 text-gray-500">
              {ENROLLMENT_LOCATION_MESSAGES.zipHelp}
            </p>
          </div>

          <LocationSelectionStatus
            isLoadingLocations={isLoadingLocations}
            isMissingDriverLicenseState={isMissingDriverLicenseState}
            isError={isError}
            hasNoLocations={hasNoLocations}
            hasNoLicenseStateLocations={hasNoLicenseStateLocations}
            driverLicenseStateName={driverLicenseStateName}
          />

          <div
            className="animate-fade-in-up relative z-[12000]"
            style={{ animationDelay: '200ms' }}
          >
            <SearchableSelect
              label="Select Training Location"
              options={locationOptions}
              value={effectiveLocation}
              onChange={handleLocationChange}
              disabled={isLocationSelectDisabled || isLocationDisabled}
              placeholder={locationPlaceholder}
              required
            />
            {selectedTransmission ? (
              <div className="mt-2 text-sm text-gray-dark">
                Transmission type: {selectedTransmission}
              </div>
            ) : null}
          </div>

          {bothAvailable ? (
            <TransmissionTypeSelector
              value={formData.transmissionType}
              errorMessage={transmissionError}
              disabled={isTransmissionDisabled}
              onChange={handleTransmissionChange}
            />
          ) : null}

          <div className="animate-fade-in-up pt-1" style={{ animationDelay: '280ms' }}>
            <Button
              type="button"
              onClick={handleContinueClick}
              fullWidth
              disabled={isContinueButtonDisabled}
              className="hover:-translate-y-0.5 active:scale-95"
            >
              Continue
            </Button>
          </div>

          {/* <div className="animate-fade-in-up" style={{ animationDelay: '340ms' }}>
            <Button
              type="button"
              variant="outline"
              fullWidth
              className="hover:-translate-y-0.5 active:scale-95"
            >
              Save Your Progress
            </Button>
          </div> */}

          <div className="animate-fade-in-up text-center" style={{ animationDelay: '400ms' }}>
            <p className="mx-auto mb-3 max-w-[400px] text-center text-[16px] font-normal leading-[1.6] text-[#707070]">
              Don't See Your Preferred Location?
              <br />
              Call Us{' '}
              <span className="font-semibold text-primary">
                <a
                  href={ENROLLMENT_SUPPORT_PHONE.href}
                  className="font-semibold text-primary transition-colors hover:text-primary-hover"
                >
                  {ENROLLMENT_SUPPORT_PHONE.display}
                </a>
              </span>
            </p>
            {/* <EnrollmentHelpText /> */}
          </div>
        </div>
      </div>
    </div>
  )
}
